import React from 'react';
import {noop} from 'lodash';
import {useMergeRefs} from '@floating-ui/react';

import {AccordionButtonView} from '../Accordion.styles';

import {ChevronLargeDownIcon, ChevronLargeRightIcon} from '../../../icons';
import {HStack} from '../../Stack/index';
import {Text} from '../../Text/Text';
import {View} from '../../View/View';
import {renderElement} from '../../../utils';

import {useAccordionButtonState} from './useAccordionButtonState';
import type {AccordionButtonProps, AccordionButtonBaseProps} from './_types';

/**
 * The layout component for the AccordionButton.
 * Used when we need to maintain UI consistency outside of an Accordion context.
 * @see - https://github.com/webflow/webflow/pull/89346
 */
export const AccordionButtonBase = React.forwardRef<
  HTMLAnchorElement,
  AccordionButtonBaseProps
>(
  (
    {
      children,
      density = 'compact',
      isDisabled,
      isOpen,
      prefix,
      size = 'medium',
      truncationProps,
      textMarginStyle,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const [anchorRef, setAnchorRef] =
      React.useState<HTMLAnchorElement | null>();
    const mergedRefs = useMergeRefs([setAnchorRef, ref]);
    const [isEscapePressed, setIsEscapePressed] = React.useState(false);
    const [shouldTooltipBeOpen, setShouldTooltipBeOpen] = React.useState(false);

    const ChevronIcon = isOpen ? ChevronLargeDownIcon : ChevronLargeRightIcon;
    const tooltipIsOpen =
      truncationProps && shouldTooltipBeOpen && !isEscapePressed;

    const truncationSettings = React.useMemo(
      () =>
        truncationProps
          ? ({
              ...truncationProps,
              truncationTooltipProps: {
                /**
                 * We want to prevent the Tooltip from opening over other Accordions.
                 * We chose `left`, since most Accordions are in the Style panel.
                 * Users can override this value for other locations if they want.
                 */
                placement: 'left',
                /**
                 * This prevents the `Tooltip` text from collapsing down too much.
                 * We chose this number because the Accordions in Style panel have 200px of usable "text" space.
                 *
                 * Anything truncated text will therefore be at least 200px wide.
                 * This minWidth shouldn't cause goofy looking Tooltips with a ton of empty space.
                 * Users can also just override this for their specific use-cases.
                 */
                minWidth: 200,
                ...truncationProps.tooltipProps,
                anchorRef,
                isOpen: tooltipIsOpen,
                /** Noop, we're relying on isActive & isEscapePressed for our change management */
                onOpenChange: noop,
              },
            } as const)
          : undefined,
      [truncationProps, anchorRef, tooltipIsOpen]
    );

    /**
     * Our `Tooltip` is controlled, because its reference element is deeply nested within our Button.
     * We need to update our tooltip open state and reset escape button press behavior on all of these events.
     */
    const handleOnMouseEnter = React.useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        setIsEscapePressed(false);
        setShouldTooltipBeOpen(true);
        onMouseEnter?.(event);
      },
      [onMouseEnter]
    );

    const handleOnMouseLeave = React.useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        setIsEscapePressed(false);
        setShouldTooltipBeOpen(false);
        onMouseLeave?.(event);
      },
      [onMouseLeave]
    );

    const handleOnFocus = React.useCallback(
      (event: React.FocusEvent<HTMLAnchorElement>) => {
        setIsEscapePressed(false);
        setShouldTooltipBeOpen(true);
        onFocus?.(event);
      },
      [onFocus]
    );

    const handleOnBlur = React.useCallback(
      (event: React.FocusEvent<HTMLAnchorElement>) => {
        setIsEscapePressed(false);
        setShouldTooltipBeOpen(false);
        onBlur?.(event);
      },
      [onBlur]
    );

    /**
     * We want to close the tooltip when the user presses escape.
     * However, it is possible for the tooltip to open when the Accordion isn't focused (e.g. on hover)
     * To achieve this, we register an event listener on document so we can grab keypresses
     */
    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsEscapePressed(true);
        }
      };

      if (tooltipIsOpen) {
        document.addEventListener('keydown', handleEscape);
      }

      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }, [tooltipIsOpen, setIsEscapePressed]);

    return (
      <AccordionButtonView
        density={density}
        isDisabled={isDisabled}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        ref={mergedRefs}
        size={size}
        {...props}
      >
        {/* min-width is necessary here to allow children to truncate in a flex context */}
        <HStack as="span" spacing={1} style={{minWidth: 0}}>
          {prefix ? renderElement(prefix) : null}
          {/* min-width is necessary here to allow children to truncate in a flex context */}
          <HStack as="span" style={{minWidth: 0}}>
            <Text font="title" style={textMarginStyle} {...truncationSettings}>
              {children}
            </Text>
          </HStack>
        </HStack>
        <View
          alignContent="center"
          display="flex"
          justifyContent="center"
          padding="$1"
        >
          <ChevronIcon colorVariant="secondary" />
        </View>
      </AccordionButtonView>
    );
  }
);

/**
 * The button that toggles the expand/collapse state of the accordion item. In most
 * cases, AccordionButton should be wrapped in an element with role heading.
 */
export const AccordionButton = React.forwardRef<
  HTMLAnchorElement,
  AccordionButtonProps
>(({id, isDisabled, onClick, ...props}, ref) => {
  const accordionStateProps = useAccordionButtonState({
    id,
    isDisabled,
    onClick,
  });

  return <AccordionButtonBase {...props} {...accordionStateProps} ref={ref} />;
});

AccordionButton.displayName = 'AccordionButton';
AccordionButton.toString = AccordionButtonView.toString;

export default AccordionButton;
